import {
  Alert,
  Button,
  ClickAwayListener,
  Divider,
  Drawer,
  FormControlLabel,
  Grid,
  Select,
  Stack,
  Switch,
  TextField,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@chronosphereio/chrono-ui';
import { parseDurationString, useFeatureFlags } from '@chronosphereio/core';
import { Autocomplete } from '@mui/material';
import { InformationOutline as InfoIcon } from 'mdi-material-ui';
import { useCallback, useMemo, useState } from 'react';
import { useImmer } from 'use-immer';
import {
  calculateAutoIntervalFormattedDuration,
  DEFAULT_INTERVAL_VARIABLE_OPTIONS,
  DEFAULT_INTERVAL_VARIABLE_SIZE,
  DEFAULT_MIN_INTERVAL_SIZE,
  INTERVAL_SIZE_LEVELS,
  isValidIntervalSizeLevel,
  ListVariableDefinition,
  ListVariableSpec,
  VariableDefinition,
} from '@/core';
import { useAnalytics } from '@/plugin-system/model';
import { useTimeRange } from '@/plugin-system/runtime';
import { DiscardChangesConfirmationDialog, ErrorBoundary } from '../../../../components';
import { PluginEditor } from '../../editing/PluginEditor';
import { VARIABLE_TYPES } from '../variable-model';
import { VariableListPreview, VariablePreview } from './VariablePreview';
import {
  getInitialState,
  getVariableDefinitionFromState,
  isPrometheusLabelValuesVariableOptions,
} from './variable-editor-form-model';

const INTERVAL_SIZE_HELP_TEXT =
  "Large interval size is recommended for rate()/increase() queries. Small interval size shows recent changes faster but may cause 'No data' with rate queries due to too few samples.";

export type Action = 'create' | 'update';

type SortValue = NonNullable<ListVariableSpec['sort']> | 'Disabled';

const SORT_OPTIONS: Array<{ key: SortValue; label: string }> = [
  { key: 'Disabled', label: 'Disabled' },
  { key: 'AlphabeticalAsc', label: 'Alphabetical (A-Z)' },
  { key: 'AlphabeticalDesc', label: 'Alphabetical (Z-A)' },
  { key: 'NumericalAsc', label: 'Numerical (1-9)' },
  { key: 'NumericalDesc', label: 'Numerical (9-1)' },
];

const TYPE_OPTIONS = VARIABLE_TYPES.map((v) => ({ key: v.kind, label: v.label }));

// TODO: Replace with proper validation library
function getValidation(state: ReturnType<typeof getInitialState>) {
  /** Name validation */
  let name = null;
  if (!state.name) {
    name = 'Name is required';
  }
  // name can only contain alphanumeric characters and underscores and no spaces
  if (state.name && !/^[a-zA-Z0-9_-]+$/.test(state.name)) {
    name = 'Name can only contain alphanumeric characters, underscores, and dashes';
  }

  return {
    name,
    isValid: !name,
  };
}

interface FallbackPreviewProps {
  error: unknown;
}

function FallbackPreview({ error }: FallbackPreviewProps) {
  if (error instanceof SyntaxError) {
    return null;
  }

  return <Alert severity="error">Error previewing values</Alert>;
}

interface VariableEditFormProps {
  initialVariableDefinition: VariableDefinition;
  onChange: (def: VariableDefinition) => void;
  onCancel: () => void;
  action?: Action;
}

const intervalOptionsPlaceholder = DEFAULT_INTERVAL_VARIABLE_OPTIONS.map((v) => `${v}`).join(', ');

export function VariableEditForm(props: VariableEditFormProps) {
  const { initialVariableDefinition, onChange, onCancel, action = 'update' } = props;
  const { enableDashboardSavedViews, enableIntervalVariableAutoSizeOption } = useFeatureFlags();
  const initialState = getInitialState(initialVariableDefinition);
  const [state, setState] = useImmer(initialState);
  const validation = useMemo(() => getValidation(state), [state]);
  const [isDiscardDialogStateOpened, setDiscardDialogStateOpened] = useState<boolean>(false);
  const [previewKey, setPreviewKey] = useState(0);
  const [regexError, setRegexError] = useState<boolean>(false);
  const analytics = useAnalytics();

  const refreshPreview = () => {
    setPreviewKey((prev) => prev + 1);
  };

  /** We use the `previewKey` that we increment to know when to explicitly update the
   * spec that will be used for preview. The reason why we do this is to avoid
   * having to re-fetch the values when the user is still editing the spec.
   *
   * We also recompute when the variable type or plugin kind changes so the
   * preview definition stays structurally valid while switching variable types.
   */
  const previewSpec = useMemo(() => {
    return getVariableDefinitionFromState(state) as ListVariableDefinition;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [previewKey, state.kind, state.listVariableFields.plugin.kind]);
  const previewPlugin = previewSpec.spec.plugin;
  const previewNeedsLabelName = useMemo(
    () =>
      state.kind === 'ListVariable' &&
      previewPlugin?.kind === 'PrometheusLabelValuesVariable' &&
      (!isPrometheusLabelValuesVariableOptions(previewPlugin.spec) || !previewPlugin.spec.label_name?.trim()),
    [state.kind, previewPlugin?.kind, previewPlugin?.spec]
  );

  // When user click on cancel, several possibilities:
  // - create action: ask for discard approval
  // - update action: ask for discard approval if changed
  const handleCancel = useCallback(() => {
    if (action === 'create' || (action === 'update' && JSON.stringify(initialState) !== JSON.stringify(state))) {
      setDiscardDialogStateOpened(true);
    } else {
      onCancel();
    }
  }, [state, initialState, action, setDiscardDialogStateOpened, onCancel]);

  const hasValidDurationOptions = useMemo(() => {
    return state.intervalVariableFields.options?.every((v) => {
      try {
        parseDurationString(v);
      } catch (_e) {
        return false;
      }
      return true;
    });
  }, [state.intervalVariableFields.options]);

  // Calculate the auto interval preview for the current time range and settings
  const { absoluteTimeRange } = useTimeRange();
  const autoIntervalPreview = useMemo(() => {
    if (!state.intervalVariableFields.auto) return null;
    // Fallback to DEFAULT_INTERVAL_VARIABLE_SIZE for existing variables without explicit size
    return calculateAutoIntervalFormattedDuration({
      timeRange: absoluteTimeRange,
      intervalSize: state.intervalVariableFields.auto_interval_size ?? DEFAULT_INTERVAL_VARIABLE_SIZE,
      minInterval: state.intervalVariableFields.auto_min_interval,
    });
  }, [
    state.intervalVariableFields.auto,
    state.intervalVariableFields.auto_interval_size,
    state.intervalVariableFields.auto_min_interval,
    absoluteTimeRange,
  ]);

  return (
    <>
      <Drawer.Header
        title={action === 'create' ? 'Add variable' : 'Edit variable'}
        primaryButtonText={action === 'create' ? 'Add' : 'Apply'}
        primaryButtonDisabled={!validation.isValid}
        onPrimaryButtonClick={() => {
          const definition = getVariableDefinitionFromState(state);

          try {
            const track = (values: Record<string, boolean>) => {
              analytics?.track('dashboard-variable_form', {
                definition_kind: definition.kind,
                action,
                ...values,
              });
            };

            if (definition.kind === 'ListVariable') {
              track({
                allow_all_value: Boolean(definition.spec.allow_all_value),
                allow_multiple: Boolean(definition.spec.allow_multiple),
                has_capturing_regexp: Boolean(definition.spec.capturing_regexp),
                has_custom_all_value: Boolean(definition.spec.custom_all_value),
              });
            } else if (definition.kind === 'IntervalVariable') {
              track({
                auto_enabled: Boolean(definition.spec.auto),
                has_auto_min_interval: Boolean(definition.spec.auto_min_interval),
                has_custom_options: Boolean(definition.spec.options?.length),
              });
            } else if (definition.kind === 'TextVariable') {
              track({});
            }
          } catch {
            // Ignore if it fails
          }

          onChange(definition);
        }}
        onSecondaryButtonClick={handleCancel}
      />
      <Drawer.Content>
        <Grid container spacing={2} mb={2}>
          <Grid item xs={8}>
            <TextField
              required
              error={!!validation.name}
              fullWidth
              label="Name"
              value={state.name}
              helperText={validation.name}
              onChange={(v) => {
                setState((draft) => {
                  draft.name = v.target.value;
                });
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <TextField
              fullWidth
              label="Display label"
              value={state.title || ''}
              onChange={(v) => {
                setState((draft) => {
                  draft.title = v.target.value;
                });
              }}
            />
          </Grid>
          <Grid item xs={8}>
            <TextField
              fullWidth
              label="Description"
              value={state.description}
              onChange={(v) => {
                setState((draft) => {
                  draft.description = v.target.value;
                });
              }}
            />
          </Grid>
          <Grid item xs={4}>
            <Select
              Label="Type"
              id="variable-type-select"
              options={TYPE_OPTIONS}
              value={TYPE_OPTIONS.find((v) => v.key === state.kind)}
              onChange={(_, option) => {
                if (option) {
                  setState((draft) => {
                    draft.kind = option.key;
                  });
                }
              }}
            />
          </Grid>
        </Grid>

        <Divider />

        {state.kind === 'TextVariable' && (
          <>
            <Typography py={1} variant="subtitle1">
              Text options
            </Typography>
            <Stack spacing={2}>
              <div>
                <VariablePreview values={[state.textVariableFields.value]} />
              </div>
              <TextField
                label="Value"
                value={state.textVariableFields.value}
                onChange={(v) => {
                  setState((draft) => {
                    draft.textVariableFields.value = v.target.value;
                  });
                }}
              />
            </Stack>
          </>
        )}

        {state.kind === 'IntervalVariable' && (
          <>
            <Typography py={1} variant="subtitle1">
              Interval options
            </Typography>
            <Stack spacing={2}>
              <Autocomplete
                onPaste={(e) => {
                  // Append new values on paste
                  const v = e.clipboardData.getData('text/plain');
                  if (v) {
                    const values = v
                      .split(',')
                      .map((s) => s.trim())
                      .filter(Boolean);
                    setState((draft) => {
                      draft.intervalVariableFields.options = [
                        ...(draft.intervalVariableFields.options ?? []),
                        ...values,
                      ];
                    });
                    e.preventDefault();
                  }
                }}
                multiple
                value={state.intervalVariableFields.options ?? []}
                onChange={(_, values) => {
                  const parsedValues = values.flatMap((v) => v.split(',').map((s) => s.trim())).filter(Boolean);

                  setState((draft) => {
                    draft.intervalVariableFields.options = parsedValues;
                  });
                }}
                options={state.intervalVariableFields.options ?? []}
                freeSolo
                clearOnBlur
                renderInput={(params) => (
                  <TextField
                    {...params}
                    error={!hasValidDurationOptions}
                    label="Interval options"
                    placeholder={`(defaults to: ${intervalOptionsPlaceholder})`}
                    helperText={
                      hasValidDurationOptions
                        ? 'Type a value (e.g. 5m) and press "Enter" to add.'
                        : 'Invalid duration format, please use a valid duration format (e.g. 5m)'
                    }
                  />
                )}
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={state.intervalVariableFields.auto ?? false}
                    onChange={(e) => {
                      setState((draft) => {
                        draft.intervalVariableFields.auto = e.target.checked;
                        // Default to 'Large' for a larger interval value, reducing rate() 'No data' issues
                        if (e.target.checked && !draft.intervalVariableFields.auto_interval_size) {
                          draft.intervalVariableFields.auto_interval_size = 'Large';
                        }
                      });
                    }}
                  />
                }
                label='Include "Auto" option'
              />

              {state.intervalVariableFields.auto === true && (
                <Stack spacing={3}>
                  {enableIntervalVariableAutoSizeOption && (
                    <Stack direction="row" spacing={1} alignItems="center">
                      <Select
                        Label="Auto interval size"
                        id="interval-auto-size-select"
                        sx={{ width: 150 }}
                        options={INTERVAL_SIZE_LEVELS.map((level) => ({ key: level, label: level }))}
                        value={{
                          // For existing variables without auto_interval_size, show 'Medium' for backwards compatibility
                          key: state.intervalVariableFields.auto_interval_size ?? DEFAULT_INTERVAL_VARIABLE_SIZE,
                          label: state.intervalVariableFields.auto_interval_size ?? DEFAULT_INTERVAL_VARIABLE_SIZE,
                        }}
                        onChange={(_, option) => {
                          if (option && isValidIntervalSizeLevel(option.key)) {
                            setState((draft) => {
                              draft.intervalVariableFields.auto_interval_size = option.key;
                            });
                          }
                        }}
                      />
                      <Tooltip Content={INTERVAL_SIZE_HELP_TEXT}>
                        <InfoIcon fontSize="inherit" color="secondary" />
                      </Tooltip>
                      {autoIntervalPreview && (
                        <Typography variant="caption" color="text.secondary">
                          Calculated interval for current time range: <strong>{autoIntervalPreview}</strong>
                        </Typography>
                      )}
                    </Stack>
                  )}
                  <TextField
                    label="Min auto step"
                    placeholder={`e.g. 10m (defaults to: ${DEFAULT_MIN_INTERVAL_SIZE})`}
                    value={state.intervalVariableFields.auto_min_interval || ''}
                    onChange={(e) => {
                      setState((draft) => {
                        if (e.target.value) {
                          draft.intervalVariableFields.auto_min_interval = e.target.value;
                        } else {
                          draft.intervalVariableFields.auto_min_interval = undefined;
                        }
                      });
                    }}
                    helperText="The minimum interval to use when auto is enabled"
                  />
                </Stack>
              )}
            </Stack>
          </>
        )}

        {state.kind === 'ListVariable' && (
          <>
            <Typography py={1} variant="subtitle1">
              List options
            </Typography>
            <Stack spacing={2} mb={2}>
              <Stack>
                {/** Hack?: Cool technique to refresh the preview to simulate onBlur event */}
                <ClickAwayListener onClickAway={() => refreshPreview()}>
                  <div />
                </ClickAwayListener>
                {/** */}
                <PluginEditor
                  width="100%"
                  pluginType="Variable"
                  pluginKindLabel="Source"
                  value={state.listVariableFields.plugin}
                  onChange={(val) => {
                    setState((draft) => {
                      draft.listVariableFields.plugin = val;
                    });
                  }}
                />
              </Stack>

              <Stack direction="row" spacing={1} alignItems="flex-start">
                <TextField
                  sx={{ width: '50%' }}
                  label="Filter or transform results using regex"
                  placeholder="Ex. instance-(.*)"
                  error={regexError}
                  helperText={regexError ? 'Invalid regular expression' : undefined}
                  value={state.listVariableFields.capturing_regexp || ''}
                  onChange={(e) => {
                    setState((draft) => {
                      if (e.target.value) {
                        // TODO: do a better fix, if empty string => it should skip the filter
                        draft.listVariableFields.capturing_regexp = e.target.value;
                      } else {
                        draft.listVariableFields.capturing_regexp = undefined;
                      }
                    });
                  }}
                />
                <Tooltip Content="Filter or transform the results using regex. A plain regex will filter the results; a regex with capture groups will transform each result. For example, (.*-)instance-(.*) transforms aws-instance-123 → aws-123.">
                  <InfoIcon
                    fontSize="inherit"
                    color="secondary"
                    sx={{ position: 'relative', top: (t) => t.spacing(1) }}
                  />
                </Tooltip>
              </Stack>

              {state.listVariableFields.plugin.kind && (
                <div>
                  {previewNeedsLabelName ? (
                    <VariablePreview values={[]} emptyMessage="Select a label name to preview values" />
                  ) : (
                    <ErrorBoundary
                      FallbackComponent={FallbackPreview}
                      resetKeys={[previewSpec]}
                      onError={(error) => {
                        if (error instanceof SyntaxError) {
                          setRegexError(true);
                        }
                      }}
                      onReset={() => setRegexError(false)}
                    >
                      <VariableListPreview definition={previewSpec} onRefresh={refreshPreview} />
                    </ErrorBoundary>
                  )}
                </div>
              )}
              <Stack>
                <Typography variant="body1">Refresh</Typography>
                <ToggleButtonGroup
                  aria-label="Refresh"
                  value={state.listVariableFields.refresh}
                  onChange={(e, val) => {
                    setState((draft) => {
                      if (!val) {
                        return;
                      }
                      draft.listVariableFields.refresh = val;
                    });
                  }}
                >
                  <ToggleButton value="DashboardLoad">On dashboard load</ToggleButton>
                  <ToggleButton value="TimeRangeUpdate">On time range change</ToggleButton>
                </ToggleButtonGroup>
                <Typography
                  mb={1}
                  variant="caption"
                  sx={(t) => ({
                    color: t.featureFlags.panwRebrandStubYear ? t.palette.text.primary : t.palette.grey[500],
                  })}
                >
                  When to update the values of this variable
                </Typography>
              </Stack>
            </Stack>
            <Divider />
            <Typography py={2} variant="subtitle1">
              Dropdown options
            </Typography>
            <Stack spacing={2}>
              <Select
                Label="Sort"
                id="variable-sort-select"
                sx={{ width: 210 }}
                options={SORT_OPTIONS}
                value={
                  SORT_OPTIONS.find((opt) => opt.key === (state.listVariableFields.sort ?? 'Disabled')) ??
                  SORT_OPTIONS[0]
                }
                onChange={(_, option) => {
                  if (option) {
                    setState((draft) => {
                      // Map 'Disabled' UI option to undefined in the actual spec
                      draft.listVariableFields.sort = option.key === 'Disabled' ? undefined : option.key;
                    });
                  }
                }}
              />
              <Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={state.listVariableFields.allowMultiple}
                      onChange={(e) => {
                        setState((draft) => {
                          draft.listVariableFields.allowMultiple = e.target.checked;
                        });
                      }}
                    />
                  }
                  label="Select multiple values"
                />
                <Typography variant="caption">Enables multiple values to be selected at the same time</Typography>
              </Stack>
              <Stack>
                <FormControlLabel
                  control={
                    <Switch
                      checked={state.listVariableFields.allowAll}
                      onChange={(e) => {
                        setState((draft) => {
                          draft.listVariableFields.allowAll = e.target.checked;
                        });
                      }}
                    />
                  }
                  label='Include "All" option'
                />
                <Typography mb={1} variant="caption">
                  {`Enables an "All" option in the dropdown`}
                </Typography>
                {state.listVariableFields.allowAll && (
                  <TextField
                    label='Custom "All" value'
                    value={state.listVariableFields.customAllValue}
                    onChange={(e) => {
                      setState((draft) => {
                        if (e.target.value) {
                          draft.listVariableFields.customAllValue = e.target.value;
                        } else {
                          draft.listVariableFields.customAllValue = undefined;
                        }
                      });
                    }}
                    helperText='Defines the value to apply when selecting the "All" option'
                  />
                )}
              </Stack>
              {state.listVariableFields.default_value && (
                <Stack spacing={1}>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Typography variant="body2">
                      Default:{' '}
                      {Array.isArray(state.listVariableFields.default_value)
                        ? state.listVariableFields.default_value.join(', ')
                        : state.listVariableFields.default_value}
                    </Typography>
                    <Button
                      size="small"
                      variant="secondary"
                      onClick={() => {
                        setState((draft) => {
                          draft.listVariableFields.default_value = undefined;
                        });
                      }}
                    >
                      Clear default
                    </Button>
                  </Stack>
                  <Typography variant="caption" color="text.secondary">
                    {enableDashboardSavedViews
                      ? 'To update the default value, use the saved views selector after selecting different variable values.'
                      : 'To change the default value, select different variable values and save the dashboard. Update defaults confirmation will be shown.'}
                  </Typography>
                </Stack>
              )}
            </Stack>
          </>
        )}
      </Drawer.Content>

      <DiscardChangesConfirmationDialog
        description="Are you sure you want to discard these changes? Changes cannot be recovered."
        isOpen={isDiscardDialogStateOpened}
        onCancel={() => {
          setDiscardDialogStateOpened(false);
        }}
        onDiscardChanges={() => {
          setDiscardDialogStateOpened(false);
          onCancel();
        }}
      />
    </>
  );
}
